import { NIL } from 'uuid';
import { Address } from './address';
import { User } from './user';

export enum ContactInfoStatus {
  New = 1,
  Entered,
}

// Explicit contact classification (item 93) — the source of truth for vendor-ness.
export enum CustomerType {
  EndUser = 0,
  Integrator = 1,
  Vendor = 2,
  Employee = 3,
}

export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  [CustomerType.EndUser]: 'End User',
  [CustomerType.Integrator]: 'B2B (Integrator)',
  [CustomerType.Vendor]: 'Vendor',
  [CustomerType.Employee]: 'Employee',
};

// item 206 — a staff member at a CRM customer (the client's internal people directory)
export interface CustomerStaffMember {
  id?: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  userId?: string; // item 212 — optional link to one of our employees; SalesPO % > 0 earns Sales PO
}

export const getEmptyStaffMember = (): CustomerStaffMember => ({
  name: '', role: '', phone: '', email: '', userId: '',
});

export interface ContactInfo {
  id: string;
  name: string;
  company: string;
  serviceAddress: Address;
  shippingAddress: Address;
  phone: string;
  alternatePhone: string;
  email: string;
  alternateEmail: string;
  // item 318 — routed-send addresses: invoices go to accountingEmail (customer),
  // purchase orders go to ordersEmail (vendor); blank falls back to the primary email.
  accountingEmail?: string;
  ordersEmail?: string;
  internalNotes: string;
  // item 647 — integrator legal identity + signatory. Edited here on the customer (the integrator);
  // RNWY/automated proposals read these read-only so legal info has ONE source of truth.
  integratorLegalName?: string;
  integratorCorporateAddress?: Address; // item 832 — structured, like serviceAddress
  integratorSignatoryName?: string;
  integratorSignatoryTitle?: string;
  integratorSignatoryEmail?: string;
  // item 717 — legal entity descriptor for contracts: "a {state} {entity type}"
  // (e.g. "a North Carolina corporation"). Entity type free-form for LLC variants.
  integratorStateOfFormation?: string;
  quickBooksName?: string; // item 943 — QB CUSTOMER segment of CUSTOMER:JOB on AR exports
  coBrandJson?: string | null; // item 979 — integrator co-brand (portal + EEULA + bids)
  integratorEntityType?: string;
  integratorEin?: string; // item 834 — the customer's EIN (federal tax id)
  // item 647 — ALTERNATE signatories (beyond the primary above), as a JSON array of
  // {name,title,email}. A proposal picks one signatory (primary + these) from a dropdown.
  integratorSignatoriesJson?: string;
  url: string;
  referredBy: string;
  // item 216 — legacy single salesRep removed (superseded by Outside/Inside, item 72)
  createdBy: User | undefined;
  createdDate: string;
  lastModifiedBy: User | undefined;
  lastModifiedDate: string;
  isVendor: boolean;
  customerType: CustomerType;
  // per-customer labor rate (item 88); null → labor bills at the catalog rate
  hourlyRate: number | null;
  taxExempt?: boolean; // item 179 — overrides per-item/line taxability
  isRnwyVerified?: boolean; // item 1085 — RNWY-verified partner badge
  pendingSignatureCount?: number; // item 1432
  taxExemptPermitNumber?: string; // item 319 — exemption permit / certificate number

  // sales attribution (item 72)
  outsideSalesRepUserId: string;
  insideSalesRepUserId: string;
  referralName: string;
  // item 190 — Referral Partner (virtual user) details captured inline; blank on save keeps
  // the partner's existing values (backend only overwrites non-null fields).
  referralEmail?: string;
  referralPhone?: string;
  referralNotionalIOSalesRate?: number | null;
  status: ContactInfoStatus;
  staff?: CustomerStaffMember[]; // item 206 — customer staff roster
}

export interface ContactDto {
  pendingSignatureCount?: number; // item 1432 — SignWell-sent papers awaiting signature
  id: string;
  name: string;
  email: string;
  phone: string;
  lastModifiedByName: string;
  isVendor: boolean;
  isFavorite: boolean; // item 237
  isRnwyVerified?: boolean; // item 1085 — RNWY-verified partner badge
  customerType?: CustomerType; // item 358 — CRM type column
  status?: ContactInfoStatus; // item 359 — accountant NEW ITEMS queue
}
export interface ContactInfoDto {
  index: ContactDto[];
  length: number;
}

export interface Vendor extends ContactInfo {
  isVendor: boolean;
}

export const getEmptyContactInfo = (): ContactInfo => ({
  alternateEmail: '',
  accountingEmail: '',
  ordersEmail: '',
  alternatePhone: '',
  company: '',
  createdBy: undefined,
  createdDate: '',
  email: '',
  id: NIL,
  internalNotes: '',
  lastModifiedBy: undefined,
  lastModifiedDate: '',
  name: '',
  phone: '',
  referredBy: '',
  integratorLegalName: '',
  // item 832 — structured, like serviceAddress/shippingAddress
  integratorCorporateAddress: {
    street: '', country: '', city: '', state: '', unit: '', zip: '',
  },
  integratorSignatoryName: '',
  integratorSignatoryTitle: '',
  integratorSignatoryEmail: '',
  integratorStateOfFormation: '',
  quickBooksName: '',
  coBrandJson: null,
  integratorEntityType: '',
  integratorEin: '', // item 834
  integratorSignatoriesJson: '',
  serviceAddress: {
    street: '', country: '', city: '', state: '', unit: '', zip: '',
  },
  shippingAddress: {
    street: '', country: '', city: '', state: '', unit: '', zip: '',
  },
  url: '',
  isVendor: false,
  customerType: CustomerType.EndUser,
  hourlyRate: null,
  taxExempt: false,
  isRnwyVerified: false,
  taxExemptPermitNumber: '',
  outsideSalesRepUserId: '',
  insideSalesRepUserId: '',
  referralName: '',
  status: ContactInfoStatus.New,
  staff: [],
});

// item 647 — a legal signatory. The customer's PRIMARY signatory lives on the contact's
// integratorSignatory* fields; ALTERNATES live in integratorSignatoriesJson (a JSON array).
export interface IntegratorSignatory { name: string; title: string; email: string; }
export const parseSignatories = (json?: string | null): IntegratorSignatory[] => {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr)
      ? arr.map((s) => ({ name: s?.name ?? '', title: s?.title ?? '', email: s?.email ?? '' }))
      : [];
  } catch {
    return [];
  }
};
// the proposal's signatory pick-list: the customer's PRIMARY (if set) + the alternates.
export const allSignatories = (c?: Partial<ContactInfo> | null): IntegratorSignatory[] => {
  if (!c) return [];
  const hasPrimary = !!(c.integratorSignatoryName || c.integratorSignatoryTitle || c.integratorSignatoryEmail);
  const primary = hasPrimary
    ? [{
      name: c.integratorSignatoryName || '',
      title: c.integratorSignatoryTitle || '',
      email: c.integratorSignatoryEmail || '',
    }]
    : [];
  return [...primary, ...parseSignatories(c.integratorSignatoriesJson)];
};
